import { guardAnalytics } from "@shared/unified-tiptap/analytics/instrument"
import { reportOperation } from "@shared/unified-tiptap/analytics/operation-event"
import type { OperationContract } from "@shared/unified-tiptap/analytics/operations"
import {
  newInteractionId,
  type EditorSurface,
  type InteractionId,
} from "@shared/unified-tiptap/analytics/types"
import { Extension } from "@tiptap/core"
import { PluginKey, Transaction } from "@tiptap/pm/state"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    operationLabel: {
      /**
       * Labels an editing operation, so that it generates newAnalyticsEvents.
       * Call this as part of a top-level `editor.chain()`, e.g.:
       *
       * ```ts
       * editor
       *   .chain()
       *   .label(OPERATION_CONTRACTS.OP_SET_CARD_COLOR, "floating-toolbar", { color, cardId })
       *   .setCardColor(color)
       *   .run()
       * ```
       *
       * For operations that don't run through a Tiptap command chain, instead use
       * `reportOperation`.
       */
      label: <Args>(
        contract: OperationContract<Args>,
        surface: EditorSurface,
        args: NoInfer<Args>,
      ) => ReturnType
    }
  }
}

/** The typed operation metadata a label stamps on its transaction. */
export interface OperationLabel {
  readonly contract: OperationContract<unknown>
  readonly surface: EditorSurface
  readonly args: unknown
  readonly interactionId: InteractionId
}

const pluginKey = new PluginKey<OperationLabel>("unifiedEditorOperationLabel")

export function getOperationLabel(tr: Transaction): OperationLabel | undefined {
  return tr.getMeta(pluginKey)
}

/**
 * Registers the `label` command, which builds newAnalyticsEvents for editing operations,
 * and dispatches them in onTransaction.
 */
export const ExtensionOperationLabel = Extension.create({
  name: "operationLabel",

  addCommands() {
    return {
      label: (contract, surface, args) => {
        return ({ tr }) => {
          const operationLabel: OperationLabel = {
            contract,
            surface,
            args,
            interactionId: newInteractionId(),
          }
          tr.setMeta(pluginKey, operationLabel)
          return true
        }
      },
    }
  },

  onTransaction({ editor, transaction }) {
    // guardAnalytics will no-op if the editor is readonly.
    guardAnalytics(editor, () => {
      const operationLabel = getOperationLabel(transaction)
      if (!operationLabel) return

      // `docChanged` is only "has steps", and a declined Tiptap command can still leave steps that cancel
      // out (Tiptap's setNode clears nodes first, then fails to set the block). Compare the documents.
      if (transaction.before.eq(transaction.doc)) return

      reportOperation(editor, operationLabel.contract, operationLabel.surface, operationLabel.args)
    })
  },
})
